import { type ContactPerson, ContactPersonSchema, type Station, StationSchema } from '@app/icare/db/schema/common'
import type { AdTeam } from '@app/icare/entities/adTeam/schema/ADTeam'
import { AdTeamSchema } from '@app/icare/entities/adTeam/schema/ADTeam'
import {
  type Article,
  type ArticleRelation,
  ArticleRelationSchema,
  ArticleSchema,
} from '@app/icare/entities/article/schema/Article'
import type { CareService } from '@app/icare/entities/careService/schema/CareService'
import { CareServiceSchema } from '@app/icare/entities/careService/schema/CareService'
import type { Clinic } from '@app/icare/entities/clinic/schema/Clinic'
import { ClinicSchema } from '@app/icare/entities/clinic/schema/Clinic'
import type { CooperationPartner } from '@app/icare/entities/cooperationPartner/schema/CooperationPartner'
import { CooperationPartnerSchema } from '@app/icare/entities/cooperationPartner/schema/CooperationPartner'
import type { CostCarrier } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import { CostCarrierSchema } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import type { Doctor } from '@app/icare/entities/doctor/schema/Doctor'
import { DoctorSchema } from '@app/icare/entities/doctor/schema/Doctor'
import type { Hospice } from '@app/icare/entities/hospice/schema/Hospice'
import { HospiceSchema } from '@app/icare/entities/hospice/schema/Hospice'
import type { MedicalSupplyHome } from '@app/icare/entities/medicalSupplyHome/schema/MedicalSupplyHome'
import { MedicalSupplyHomeSchema } from '@app/icare/entities/medicalSupplyHome/schema/MedicalSupplyHome'
import type { MedicalTechnology } from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import { MedicalTechnologySchema } from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import type { NursingHome } from '@app/icare/entities/nursingHome/schema/NursingHome'
import { NursingHomeSchema } from '@app/icare/entities/nursingHome/schema/NursingHome'
import type { Patient } from '@app/icare/entities/patient/schema/Patient'
import { PatientSchema } from '@app/icare/entities/patient/schema/Patient'
import type { PatientADTeamRelation } from '@app/icare/entities/patient/schema/PatientADTeam'
import { PatientADTeamRelationSchema } from '@app/icare/entities/patient/schema/PatientADTeam'
import type { PatientAddressRelation } from '@app/icare/entities/patient/schema/PatientAddress'
import { PatientAddressRelationSchema } from '@app/icare/entities/patient/schema/PatientAddress'
import type { PatientApplicantRelation } from '@app/icare/entities/patient/schema/PatientApplicant'
import { PatientApplicantRelationSchema } from '@app/icare/entities/patient/schema/PatientApplicant'
import type { PatientCareLevelRelation } from '@app/icare/entities/patient/schema/PatientCareLevel'
import { PatientCareLevelRelationSchema } from '@app/icare/entities/patient/schema/PatientCareLevel'
import type { PatientCareManagerRelation } from '@app/icare/entities/patient/schema/PatientCareManager'
import { PatientCareManagerRelationSchema } from '@app/icare/entities/patient/schema/PatientCareManager'
import {
  type PatientCareServiceContactPerson,
  PatientCareServiceContactPersonSchema,
  type PatientCareServiceRelation,
  PatientCareServiceRelationSchema,
} from '@app/icare/entities/patient/schema/PatientCareService'
import {
  type PatientClinicContactPerson,
  PatientClinicContactPersonSchema,
  type PatientClinicRelation,
  PatientClinicRelationSchema,
  type PatientClinicStation,
  PatientClinicStationSchema,
} from '@app/icare/entities/patient/schema/PatientClinic'
import type { PatientCooperationPartnerRelation } from '@app/icare/entities/patient/schema/PatientCooperationPartner'
import { PatientCooperationPartnerRelationSchema } from '@app/icare/entities/patient/schema/PatientCooperationPartner'
import {
  type PatientCostCarrierContactPerson,
  PatientCostCarrierContactPersonSchema,
  type PatientCostCarrierRelation,
  PatientCostCarrierRelationSchema,
} from '@app/icare/entities/patient/schema/PatientCostCarrier'
import {
  type PatientCostCarrierExemption,
  PatientCostCarrierExemptionSchema,
} from '@app/icare/entities/patient/schema/PatientCostCarrierExemption'
import {
  type PatientDoctorContactPerson,
  PatientDoctorContactPersonSchema,
  type PatientDoctorRelation,
  PatientDoctorRelationSchema,
} from '@app/icare/entities/patient/schema/PatientDoctor'
import type { PatientMedicalSupplyAccessRelation } from '@app/icare/entities/patient/schema/PatientMedicalSupplyAccess'
import { PatientMedicalSupplyAccessRelationSchema } from '@app/icare/entities/patient/schema/PatientMedicalSupplyAccess'
import {
  type PatientMedicalTechnologyContactPerson,
  PatientMedicalTechnologyContactPersonSchema,
  type PatientMedicalTechnologyRelation,
  PatientMedicalTechnologyRelationSchema,
} from '@app/icare/entities/patient/schema/PatientMedicalTechnology'
import type { PatientMobilityRelation } from '@app/icare/entities/patient/schema/PatientMobility'
import { PatientMobilityRelationSchema } from '@app/icare/entities/patient/schema/PatientMobility'
import {
  type PatientPharmacyContactPerson,
  PatientPharmacyContactPersonSchema,
  type PatientPharmacyRelation,
  PatientPharmacyRelationSchema,
} from '@app/icare/entities/patient/schema/PatientPharmacy'
import type { PatientRelativeRelation } from '@app/icare/entities/patient/schema/PatientRelative'
import { PatientRelativeRelationSchema } from '@app/icare/entities/patient/schema/PatientRelative'
import type { PatientSeparateResidenceRelation } from '@app/icare/entities/patient/schema/PatientSeparateResidence'
import { PatientSeparateResidenceRelationSchema } from '@app/icare/entities/patient/schema/PatientSeparateResidence'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import { PatientTherapyRelationSchema } from '@app/icare/entities/patient/schema/PatientTherapy'
import type { PatientWeightRelation } from '@app/icare/entities/patient/schema/PatientWeight'
import { PatientWeightRelationSchema } from '@app/icare/entities/patient/schema/PatientWeight'
import type { PatientForm } from '@app/icare/entities/patientForm/schema/PatientForm'
import { PatientFormSchema } from '@app/icare/entities/patientForm/schema/PatientForm'
import type { Pharmacy } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import { PharmacySchema } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import type { SyncableUser } from '@app/icare/entities/user/schema/User'
import { UserSchema } from '@app/icare/entities/user/schema/User'
import type { Kysely } from 'kysely'

export const CommonDatabaseSchema = {
  patient: PatientSchema,
  patientAddress: PatientAddressRelationSchema,
  patientMobility: PatientMobilityRelationSchema,
  patientWeight: PatientWeightRelationSchema,
  patientCareLevel: PatientCareLevelRelationSchema,
  patientCareService: PatientCareServiceRelationSchema,
  patientRelative: PatientRelativeRelationSchema,
  patientTherapy: PatientTherapyRelationSchema,
  patientApplicant: PatientApplicantRelationSchema,
  patientPharmacy: PatientPharmacyRelationSchema,
  patientMedicalTechnology: PatientMedicalTechnologyRelationSchema,
  patientCooperationPartner: PatientCooperationPartnerRelationSchema,
  patientCostCarrier: PatientCostCarrierRelationSchema,
  patientCostCarrierExemption: PatientCostCarrierExemptionSchema,
  patientSeparateResidence: PatientSeparateResidenceRelationSchema,
  patientClinic: PatientClinicRelationSchema,
  patientDoctor: PatientDoctorRelationSchema,
  patientMedicalSupplyAccess: PatientMedicalSupplyAccessRelationSchema,
  patientAdTeam: PatientADTeamRelationSchema,
  patientCareManager: PatientCareManagerRelationSchema,
  article: ArticleSchema,
  articleRelation: ArticleRelationSchema,
  adTeam: AdTeamSchema,
  careService: CareServiceSchema,
  clinic: ClinicSchema,
  cooperationPartner: CooperationPartnerSchema,
  costCarrier: CostCarrierSchema,
  doctor: DoctorSchema,
  user: UserSchema,
  hospice: HospiceSchema,
  medicalSupplyHome: MedicalSupplyHomeSchema,
  medicalTechnology: MedicalTechnologySchema,
  nursingHome: NursingHomeSchema,
  pharmacy: PharmacySchema,
  patientForm: PatientFormSchema,
  contactPerson: ContactPersonSchema,
  station: StationSchema,
  patientClinicContactPerson: PatientClinicContactPersonSchema,
  patientClinicStation: PatientClinicStationSchema,
  patientDoctorContactPerson: PatientDoctorContactPersonSchema,
  patientPharmacyContactPerson: PatientPharmacyContactPersonSchema,
  patientCareServiceContactPerson: PatientCareServiceContactPersonSchema,
  patientMedicalTechnologyContactPerson: PatientMedicalTechnologyContactPersonSchema,
  patientCostCarrierContactPerson: PatientCostCarrierContactPersonSchema,
} as const

/**
 * Manually spelled out instead of using a mapped type with `z.infer<>` to avoid
 * forcing the type checker to resolve all 35+ Zod schemas (including discriminated
 * unions) simultaneously every time this type is referenced. This is a significant
 * performance optimization for IDE type resolution (IntelliJ, VS Code).
 *
 * When adding a new schema to CommonDatabaseSchema, add the corresponding type here too.
 */
export interface AppCommonDatabase {
  patient: Patient
  patientAddress: PatientAddressRelation
  patientMobility: PatientMobilityRelation
  patientWeight: PatientWeightRelation
  patientCareLevel: PatientCareLevelRelation
  patientCareService: PatientCareServiceRelation
  patientRelative: PatientRelativeRelation
  patientTherapy: PatientTherapyRelation
  patientApplicant: PatientApplicantRelation
  patientPharmacy: PatientPharmacyRelation
  patientMedicalTechnology: PatientMedicalTechnologyRelation
  patientCooperationPartner: PatientCooperationPartnerRelation
  patientCostCarrier: PatientCostCarrierRelation
  patientCostCarrierExemption: PatientCostCarrierExemption
  patientSeparateResidence: PatientSeparateResidenceRelation
  patientClinic: PatientClinicRelation
  patientDoctor: PatientDoctorRelation
  patientMedicalSupplyAccess: PatientMedicalSupplyAccessRelation
  patientAdTeam: PatientADTeamRelation
  patientCareManager: PatientCareManagerRelation
  article: Article
  articleRelation: ArticleRelation
  adTeam: AdTeam
  careService: CareService
  clinic: Clinic
  cooperationPartner: CooperationPartner
  costCarrier: CostCarrier
  doctor: Doctor
  user: SyncableUser
  hospice: Hospice
  medicalSupplyHome: MedicalSupplyHome
  medicalTechnology: MedicalTechnology
  nursingHome: NursingHome
  pharmacy: Pharmacy
  patientForm: PatientForm
  contactPerson: ContactPerson
  station: Station
  patientClinicContactPerson: PatientClinicContactPerson
  patientClinicStation: PatientClinicStation
  patientDoctorContactPerson: PatientDoctorContactPerson
  patientPharmacyContactPerson: PatientPharmacyContactPerson
  patientCareServiceContactPerson: PatientCareServiceContactPerson
  patientMedicalTechnologyContactPerson: PatientMedicalTechnologyContactPerson
  patientCostCarrierContactPerson: PatientCostCarrierContactPerson
}

export type CommonDatabaseType<DB extends AppCommonDatabase = AppCommonDatabase> = Kysely<DB>
